import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Skeleton, Tag, Typography, message } from "antd";
import type React from "react";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import { useIsMobileView } from "../../../hooks/ui-hook";
import { deleteCustomNote } from "../../../service/notes.service";
import { setCustomTemplateSelectedTab } from "../../../slices/appStateSlice";
import { LC_KEYS } from "../../../utils/constants";
import styles from "./AITemplateModal.module.scss";
import { type CustomNote, TabType } from "./types";

const List = ({
  handleEditTemplate,
  handleDuplicateTemplate,
  duplicatingPk,
  customNoteList,
  fetchCustomNoteList,
  isListLoading,
  isSharedList = false,
  isCompact = false,
}: {
  setIsPrimaryButtonDisabled?: (disabled: boolean) => void;
  handleEditTemplate: (template: CustomNote) => void;
  handleDuplicateTemplate: (template: CustomNote) => void;
  /** pk of the template being copied right now, so only that card's button spins. */
  duplicatingPk: number | null;
  isListLoading: boolean;
  customNoteList: CustomNote[];
  fetchCustomNoteList: () => void;
  isSharedList?: boolean;
  /** Drops the list's 50vh min-height — used while searching so a single hit doesn't leave a
   * screen of whitespace between it and the rest of the drawer. */
  isCompact?: boolean;
}) => {
  const componentName = "ai-template-modal";
  const dispatch = useAppDispatch();

  async function handleDeleteTemplate(pk: number, id: number, e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    await deleteCustomNote(pk)
      .then(() => {
        const selectedNote = localStorage.getItem(LC_KEYS.NOTE_TYPE);
        if (selectedNote?.toString() === id.toString()) {
          localStorage.removeItem(LC_KEYS.NOTE_TYPE);
          localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
        }
        message.success("Your template has been deleted");
        fetchCustomNoteList();
      })
      .catch((err) => {
        message.error("Failed to delete template");
        console.log(err);
      });
  }

  function EmptyState() {
    const isMobile = useIsMobileView();
    return (
      <div className={styles[`${componentName}__empty-state-container`]}>
        <div className={styles[`${componentName}__empty-state-icon-container`]}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={styles[`${componentName}__empty-state-icon`]}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z"
            />
          </svg>
        </div>

        {!isMobile && (
          <>
            <div className={styles[`${componentName}__empty-state-text`]}>
              <Typography.Title level={4}>Create your first custom template using Nudge's AI</Typography.Title>
              <Typography.Text>
                Paste your ideal note and let Nudge's AI create a custom template for you.
              </Typography.Text>
            </div>
          </>
        )}

        <div className={styles[`${componentName}__empty-state-button-container`]}>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => dispatch(setCustomTemplateSelectedTab(TabType.CREATE))}
          >
            Create Template
          </Button>
        </div>
      </div>
    );
  }

  if (isListLoading) {
    return (
      <div style={{ paddingInline: "24px", marginTop: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <Skeleton.Button active style={{ width: "550px", height: "92px", borderRadius: "16px" }} />
        <Skeleton.Button active style={{ width: "550px", height: "92px", borderRadius: "16px" }} />
        <Skeleton.Button active style={{ width: "550px", height: "92px", borderRadius: "16px" }} />
      </div>
    );
  }

  if (!customNoteList) {
    return null;
  }

  if (customNoteList.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      className={`${styles[`${componentName}__list-container`]} ${
        isCompact ? styles[`${componentName}__list-container-compact`] : ""
      }`}
    >
      {customNoteList.map((template) => (
        <div key={template.id} className={styles[`${componentName}__list-item-container`]}>
          <button
            onClick={() => {
              if (template.status !== 1) {
                handleEditTemplate(template);
              }
            }}
            style={{
              cursor: template.status === 1 ? "default" : "pointer",
              pointerEvents: template.status === 1 ? "none" : "auto",
              display: "flex",
              textAlign: "left",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                paddingTop: "5px",
              }}
            >
              <span className={styles[`${componentName}__list-item`]}>{template.name}</span>
              <span className={styles[`${componentName}__list-item-document`]}>{template.document}</span>
            </div>
          </button>
          <div className={styles[`${componentName}__list-item-actions-container`]}>
            <ActionContainer
              template={template}
              is_group_accessible={template.is_group_accessible}
              handleDeleteTemplate={handleDeleteTemplate}
              handleEditTemplate={handleEditTemplate}
              handleDuplicateTemplate={handleDuplicateTemplate}
              isDuplicating={duplicatingPk === template.pk}
              isSharedList={isSharedList}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default List;

function ActionContainer({
  template,
  is_group_accessible,
  handleDeleteTemplate,
  handleEditTemplate,
  handleDuplicateTemplate,
  isDuplicating,
  isSharedList = false,
}: {
  template: CustomNote;
  is_group_accessible?: boolean;
  handleDeleteTemplate: (pk: number, id: number, e?: React.MouseEvent<HTMLElement>) => void;
  handleEditTemplate: (template: CustomNote) => void;
  handleDuplicateTemplate: (template: CustomNote) => void;
  isDuplicating: boolean;
  isSharedList?: boolean;
}) {
  const componentName = "ai-template-modal";

  return (
    <>
      <div className={styles[`${componentName}__list-item-status`]}>
        {/* Conditionally render Tag for sharing status */}
        {is_group_accessible && (
          <Tag color="blue" style={{ fontSize: "0.8em", marginInlineEnd: 0 }}>
            Shared with Group
          </Tag>
        )}
        {template.status === 2 && <Typography.Text style={{ color: "#FF4D4F" }}>Errored</Typography.Text>}
      </div>
      <div className={styles[`${componentName}__list-item-actions`]}>
        {/* Edit a shared template forks it into a copy of your own — see handleEditTemplate. */}
        {/* Outlined in the theme's primary, mirroring the way Delete is outlined in danger. */}
        <Button color="primary" variant="outlined" icon={<EditOutlined />} onClick={() => handleEditTemplate(template)}>
          Edit
        </Button>
        <Button icon={<CopyOutlined />} loading={isDuplicating} onClick={() => handleDuplicateTemplate(template)}>
          Duplicate
        </Button>
        {!isSharedList && (
          <Popconfirm
            title="Delete Template"
            description="Are you sure to delete this template?"
            onConfirm={(e?: React.MouseEvent<HTMLElement>) => handleDeleteTemplate(template.pk, template.id, e)}
            okText="Yes"
            onCancel={(e) => e?.stopPropagation()}
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>
    </>
  );
}
